import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, isRTL, type SupportedLanguage } from '../i18n';

interface LanguageContextType {
  currentLanguage: SupportedLanguage;
  isRTL: boolean;
  direction: 'ltr' | 'rtl';
  changeLanguage: (language: SupportedLanguage) => Promise<void>;
  supportedLanguages: readonly SupportedLanguage[];
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState<SupportedLanguage>(() => {
    const stored = localStorage.getItem('i18nextLng');
    return (SUPPORTED_LANGUAGES.includes(stored as SupportedLanguage) 
      ? stored 
      : 'en') as SupportedLanguage;
  });

  const isCurrentRTL = isRTL(currentLanguage);
  const direction: 'ltr' | 'rtl' = isCurrentRTL ? 'rtl' : 'ltr';

  // Update document attributes when language changes
  useEffect(() => {
    const updateDocumentAttributes = () => {
      const html = document.documentElement;
      
      // Update language attribute
      html.lang = currentLanguage;
      
      // Update direction attribute and classes
      html.dir = direction;
      html.classList.toggle('rtl', isCurrentRTL);
      html.classList.toggle('ltr', !isCurrentRTL);
      
      // Update font family class for Arabic
      if (currentLanguage === 'ar') {
        html.classList.add('font-arabic');
        html.classList.remove('font-latin');
      } else {
        html.classList.add('font-latin');
        html.classList.remove('font-arabic');
      }
      
      // Add or remove RTL-specific body classes
      document.body.classList.toggle('rtl', isCurrentRTL);
      document.body.classList.toggle('ltr', !isCurrentRTL);
    };

    updateDocumentAttributes();
  }, [currentLanguage, direction, isCurrentRTL]);

  // Listen to i18n language changes
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      const newLanguage = lng as SupportedLanguage;
      if (SUPPORTED_LANGUAGES.includes(newLanguage)) {
        setCurrentLanguage(newLanguage);
      }
    };

    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  // Initialize with stored language preference
  useEffect(() => {
    const initializeLanguage = async () => {
      const storedLang = localStorage.getItem('i18nextLng');
      if (storedLang && SUPPORTED_LANGUAGES.includes(storedLang as SupportedLanguage)) {
        if (i18n.language !== storedLang) {
          setIsLoading(true);
          try {
            await i18n.changeLanguage(storedLang);
          } catch (error) {
            console.error('Failed to initialize language:', error);
          } finally {
            setIsLoading(false);
          }
        }
      }
    };

    initializeLanguage();
  }, [i18n]);

  const changeLanguage = async (language: SupportedLanguage) => {
    if (language === currentLanguage) return;
    
    setIsLoading(true);
    try {
      await i18n.changeLanguage(language);
      localStorage.setItem('i18nextLng', language);
      setCurrentLanguage(language);
    } catch (error) {
      console.error('Failed to change language:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const contextValue: LanguageContextType = {
    currentLanguage,
    isRTL: isCurrentRTL,
    direction,
    changeLanguage,
    supportedLanguages: SUPPORTED_LANGUAGES,
    isLoading,
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageProvider;